import { getDefaultOpacity } from '@/utils/layers/styleFactory/opacity'
import { ILayerValue } from '@lifk/types'

export const colors = {
  // primary
  white: 'ffffff',
  poppy: 'FAB411',
  slate: '3A3D40',
  slateTint1: '616466',
  slateTint2: '898B8C',
  slateTint3: '9D9EA0',
  slateTint5: 'D8D8D9',
  charcoal: '17181A',
  stone: 'faf9f7',

  // secondary
  sky: '8EABD8',
  skyTone2: '334B79',
  alluvium: 'E0F6F6',
  alluviumTone3: '458485',
  manzanita: '802225',
  manzanitaTint1: 'BB4644',

  // accents
  fire: 'FF6333',

  // system
  error: 'B60012',
  warning: 'F99319',
  success: '10611D',
  successTint1: '64CF56',

  // other
  coulter: '1A613B',
  coulterTone1: '#3472E'
}

export const colorHex = (color: keyof typeof colors) => `#${colors[color]}`

export const defaultDataLayerColor = colorHex('slateTint2')

/*
  General Utils
*/
export const getChipColors = (layerValue: ILayerValue) => {
  const color = layerValue.styleConfig.color
  const colorWithOpacity = hexToRGBA(
    color,
    layerValue.styleConfig.opacity ?? getDefaultOpacity(layerValue.style)
  )
  if (color === 'targetProperty') {
    // for Tribal Areas layer
    return {
      colorWithOpacity: defaultDataLayerColor,
      color: defaultDataLayerColor
    }
  }
  if (layerValue.style.startsWith('Polygon') || layerValue.style === 'Raster') {
    // add opacity for polygons
    return { colorWithOpacity, color }
  }

  // default to no opacity for everything else
  return { colorWithOpacity: color, color }
}

/*
  Color Format Conversions
*/
export const hexToRGBNumbers = (hexRaw: string) => {
  const hex = hexRaw.trim()
  if (hex.length < 7) return

  // Convert each hex character pair into an integer
  const red = parseInt(hex.substring(1, 3), 16)
  const green = parseInt(hex.substring(3, 5), 16)
  const blue = parseInt(hex.substring(5, 7), 16)

  return { red, green, blue }
}

export const hexToRGBA = (hex: string, opacity: number) => {
  const rgb = hexToRGBNumbers(hex)
  if (!rgb) return

  // Concatenate these codes into proper RGBA format
  return `rgba(${rgb.red}, ${rgb.green}, ${rgb.blue}, ${opacity})`
}

export const hexToHSL = (hex: string) => {
  const rgb = hexToRGBNumbers(hex)
  if (!rgb) return

  rgb.red /= 255
  rgb.green /= 255
  rgb.blue /= 255

  const cmin = Math.min(rgb.red, rgb.green, rgb.blue)
  const cmax = Math.max(rgb.red, rgb.green, rgb.blue)
  const delta = cmax - cmin

  let h = 0
  if (delta === 0) {
    h = 0
  } else if (cmax === rgb.red) {
    h = ((rgb.green - rgb.blue) / delta) % 6
  } else if (cmax === rgb.green) {
    h = (rgb.blue - rgb.red) / delta + 2
  } else {
    h = (rgb.red - rgb.green) / delta + 4
  }
  h = Math.round(h * 60)
  if (h < 0) {
    h += 360
  }

  let l = (cmax + cmin) / 2
  let s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1))
  s = +(s * 100).toFixed(1)
  l = +(l * 100).toFixed(1)

  return { h, s, l }
}
